import React, { useState, useRef, useEffect, useContext } from 'react';
import api from '../../axios/api';
import ReactLoading from "react-loading";
import {
  FaCalendarPlus,
  FaCheckCircle,
  FaClock,
  FaPagelines,
  FaTree,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaUpload,
  FaExclamationTriangle,
} from "react-icons/fa";
import { FaTimesCircle } from 'react-icons/fa';
import "../../assets/NewFiles/Css/profilePosts.css";
import Select from 'react-select';
import { FaUserAlt, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, IconButton } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import PopupMap from '../features/PopupMap';
import EXIF from "exif-js";
import { toast, ToastContainer } from "react-toastify";
import { GlobalContext } from "../../contexts/GlobalContext";
import imageCompression from "browser-image-compression";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Button from "../common/styled components/Button";

const ProfilePosts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    treeName: false,
    uploadedBy: false,
    location: false,
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPostId, setEditingPostId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState("");
  const [image, setImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const {
    address,
    setAddress,
    latlng,
    setLatlng,
    country,
    setCountry,
    setLoggedIn,
    isLoggedIn,
    role,
    addressData,
    setAddressData
  } = useContext(GlobalContext);

  const [plantSpeciesOptions, setPlantSpeciesOptions] = useState([]);
  const [ageOptions] = useState([
    { value: "1W", label: "1 Week" },
    { value: "6M", label: "1 - 6 Months" },
    { value: "12M", label: "1 Year" },
    { value: "36M", label: "2 - 3 Years" },
    { value: "60M", label: "4 - 5 Years" },
    { value: "96M", label: "6 - 8 Years" },
    { value: "144M", label: "9 - 12 Years" },
    { value: "156M", label: "13 + Years" },
  ]);

  const fileInputRef = useRef(null);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    component: null
  });

  const [editFormData, setEditFormData] = useState({
    tree_id: '',
    tree_history_id: '',
    treeName: '',
    age: '',
    speciesLabel: '',
    speciesId: '',
    treeImageUrl: '',
    diameter: '',
    height: '',
    address: '',
    plantedOn: '',
    lat: '',
    lng: '',
    country: ''
  });

  // Function to handle modal opening with different components
  const openModal = (title, component) => {
    // Set the current location values for the map before opening it
    if (component === PopupMap && editFormData.lat && editFormData.lng) {
      setLatlng({
        lat: parseFloat(editFormData.lat),
        lng: parseFloat(editFormData.lng)
      });
      setAddress(editFormData.address);
      setCountry(editFormData.country || '');
    }

    setModalConfig({
      isOpen: true,
      title,
      component
    });
  };

  const closeModal = () => {
    // After closing the map modal, update the form data with the new location
    if (modalConfig.component === PopupMap && address) {
      setEditFormData(prev => ({
        ...prev,
        address: address,
        lat: latlng?.lat?.toString() || '',
        lng: latlng?.lng?.toString() || '',
        country: country || ''
      }));

      // Clear the formError for address if it exists
      if (formErrors.address) {
        setFormErrors(prev => ({
          ...prev,
          address: null
        }));
      }
    }

    setModalConfig({
      isOpen: false,
      title: "",
      component: null
    });
  };

  const renderComponent = () => {
    const Component = modalConfig.component;
    return Component ? <Component onClose={closeModal} /> : null;
  };

  useEffect(() => {
    const checkUserAuth = async () => {
      try {
        const response = await api.get('/api/user/userdetails');

        if (response.data.isLoggedIn) {
          const { posts } = response.data.user;
          setPosts(posts);
        }
      } catch (error) {
        console.error('Error checking user auth', error);
      } finally {
        setLoading(false);
      }
    };

    checkUserAuth();
  }, []);

  useEffect(() => {
    const fetchPlantSpecies = async () => {
      try {
        const response = await api.get("/api/user/plantSpecies");
        const speciesData = response.data.map((species) => ({
          value: species.id,
          label: species.label,
          code: species.code,
          commonName: species.common_name
        }));

        setPlantSpeciesOptions(speciesData);
      } catch (error) {
        console.error("Error fetching plant species:", error);
      }
    };

    fetchPlantSpecies();
  }, []);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFilters({
      ...filters,
      [name]: checked,
    });
  };

  const handleEditClick = (post) => {
    setEditingPostId(post.tree_id);

    // Parse latitude and longitude from post data
    const lat = post.lat || '';
    const lng = post.lng || '';

    // Find the species option that matches the post's speciesLabel
    const speciesOption = plantSpeciesOptions.find(
      option => option.label === post.speciesLabel || option.commonName === post.speciesLabel
    );

    setEditFormData({
      id: post.id,
      tree_history_id: post.tree_history_id,
      tree_id: post.tree_id,
      treeName: post.treeName,
      age: post.age,
      ageLabel: getAgeLabelFromValue(post.age),
      speciesId: speciesOption?.value || '',
      speciesLabel: speciesOption?.label || post.speciesLabel,
      treeImageUrl: post.treeImageUrl,
      diameter: post.dia_breast_height || '',
      height: post.height || '',
      address: post.address,
      plantedOn: post.plantedOn,
      lat: lat,
      lng: lng,
      country: post.country || ''
    });

    // Clear any previous form errors
    setFormErrors({});

    // Set preview image
    setPreviewImage(post.treeImageUrl);
  };

  const getAgeLabelFromValue = (ageValue) => {
    const option = ageOptions.find(opt => opt.value === ageValue);
    return option ? option.label : ageValue;
  };

  const handleCancelEdit = () => {
    setEditingPostId(null);
    setSelectedImage(null);
    setPreviewImage(null);
    setFormErrors({});
  };

  const handleImageUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];

    // Check if the file is an image
    if (!file || (!file.type.startsWith("image/") && file.type !== "image/webp")) {
      setError("Please upload a valid image file.");
      toast.error("Please upload a valid image file.");
      e.target.value = "";
      return;
    }

    // Function to read EXIF data before compression
    const readExifData = () => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = function () {
            EXIF.getData(img, function () {
              const lat = EXIF.getTag(this, "GPSLatitude");
              const lon = EXIF.getTag(this, "GPSLongitude");
              if (lat && lon) {
                console.log(
                  "Raw EXIF data - Latitude:",
                  lat,
                  "Longitude:",
                  lon
                );
                resolve({ lat, lon, img });
              } else {
                reject(new Error("No GPS data found in EXIF"));
              }
            });
          };
          img.src = event.target.result;
        };
        reader.readAsDataURL(file);
      });
    };

    let exifData;
    try {
      // Try to read EXIF data for geolocation
      exifData = await readExifData();

      // Get reference direction (N, S, E, W)
      const latRef = EXIF.getTag(exifData.img, "GPSLatitudeRef") || "N";
      const lonRef = EXIF.getTag(exifData.img, "GPSLongitudeRef") || "W";

      // Calculate the latitude and longitude in decimal format
      const latitude =
        (exifData.lat[0] + exifData.lat[1] / 60 + exifData.lat[2] / 3600) *
        (latRef === "N" ? 1 : -1);
      const longitude =
        (exifData.lon[0] + exifData.lon[1] / 60 + exifData.lon[2] / 3600) *
        (lonRef === "W" ? -1 : 1);

      setLatlng({ lat: latitude, lng: longitude });
      // Fetch address data from the calculated coordinates
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&accept-language=en&lat=${latitude}&lon=${longitude}`
      );
      if (!response.ok) {
        throw new Error("Unable to fetch address data");
      }
      const data = await response.json();
      setAddress(data.display_name);
      setCountry(data.address.country);
      setAddressData(data.address);

      // Update form data with location information
      setEditFormData(prev => ({
        ...prev,
        address: data.display_name,
        lat: latitude.toString(),
        lng: longitude.toString(),
        country: data.address.country
      }));

    } catch (error) {
      if (error.message === "No GPS data found in EXIF") {
        console.warn("Warning:", error.message);
        setError("No geolocation data found in the image.");
        toast.warn("No geolocation data found in the image.");
        setAddress("");
        setCountry("");
      } else if (error.message === "Unable to fetch address data") {
        console.error("Error:", error.message);
        setError("Failed to retrieve address data from coordinates.");
        toast.error("Failed to retrieve address data from coordinates.");
        setAddress("");
        setCountry("");
      } else {
        console.error("Error processing image:", error.message);
        setError("Failed to process geolocation.");
        toast.error("Failed to process geolocation.");
        setAddress("");
        setCountry("");
      }

      setLatlng(null);
      setError("No geolocation data found in the image.");
    }

    try {
      const options = {
        maxSizeMB: 1,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      setImage(compressedFile);
      setSelectedImage(compressedFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(compressedFile);

      toast.success("Image processed successfully!");
    } catch (compressionError) {
      console.error("Error compressing image:", compressionError.message);
      setError("Failed to compress the image.");
      toast.error("Failed to compress the image.");
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!editFormData.treeName.trim()) errors.treeName = "Tree name is required";
    if (!editFormData.age) errors.age = "Age is required";
    if (!editFormData.speciesId) errors.species = "Species is required";
    if (!editFormData.address.trim()) errors.address = "Location is required";

    if (!editFormData.plantedOn) {
      errors.plantedOn = "Planted date is required";
    } else {
      const plantedDate = new Date(editFormData.plantedOn);
      const now = new Date();
      if (plantedDate > now) {
        errors.plantedOn = "Planted date cannot be in the future";
      }
    }

    // Validate diameter and height (required fields)
    if (!editFormData.diameter) errors.diameter = "Diameter is required";
    else if (isNaN(editFormData.diameter)) errors.diameter = "Diameter must be a number";

    if (!editFormData.height) errors.height = "Height is required";
    else if (isNaN(editFormData.height)) errors.height = "Height must be a number";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/user/userdetails');

      if (response.data.isLoggedIn) {
        const { posts } = response.data.user;
        setPosts(posts);
      }
    } catch (error) {
      console.error('Error fetching posts', error);
      toast.error('Failed to refresh tree data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      // Validate form fields
      if (!validateForm()) {
        return;
      }

      // Create a FormData object for the API request
      const formData = new FormData();

      // Add all form fields to the FormData
      for (const key in editFormData) {
        if (editFormData[key] !== undefined && editFormData[key] !== null) {
          formData.append(key, editFormData[key]);
        }
      }

      // Add the image file if one was selected
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      // Here you would make an API call to update the post
      setLoading(true);
      const response = await api.put(`/api/upload/update/${editFormData.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update local state
      const updatedPosts = posts.map(post => {
        if (post.tree_id === editFormData.tree_id) {
          return {
            ...post,
            ...editFormData,
            treeImageUrl: previewImage || post.treeImageUrl,
            // Include location data
            address: editFormData.address,
            lat: editFormData.lat,
            lng: editFormData.lng,
            country: editFormData.country,
            // Update species label
            speciesLabel: plantSpeciesOptions.find(
              opt => opt.value === editFormData.speciesId
            )?.label || editFormData.speciesLabel
          };
        }
        return post;
      });

      setPosts(updatedPosts);
      setEditingPostId(null);
      setSelectedImage(null);
      setPreviewImage(null);
      setFormErrors({});

      // Show success message
      toast.success('Tree information updated successfully');
      setLoading(false);
      // Refresh posts to reflect changes
      await fetchPosts();
    } catch (error) {
      setLoading(false);
      console.error('Error updating post', error);
      toast.error('Failed to update tree information');
    }
  };

  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    try {
      // Here you would make an API call to delete the post
      setLoading(true);
      await api.delete(`/api/upload/delete/${postToDelete.id}`);

      // Update local state
      setPosts(posts.filter(post => post.tree_id !== postToDelete.tree_id));

      // Show success message
      toast.success('Tree post deleted successfully');

      // Refetch posts to ensure UI is in sync with backend
      await fetchPosts();
    } catch (error) {
      console.error('Error deleting post', error);
      toast.error('Failed to delete tree post');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
  };

  const handleEditFormChange = (e, isSelect, selectedOption, fieldName) => {
    if (isSelect) {
      // Handle react-select change
      setEditFormData({
        ...editFormData,
        [fieldName]: selectedOption.value, // Store the value directly
        [`${fieldName}Id`]: selectedOption.value, // Also store as ID if needed
        [`${fieldName}Label`]: selectedOption.label // Store the label for display
      });

      // Clear error for this field
      if (formErrors[fieldName]) {
        setFormErrors({
          ...formErrors,
          [fieldName]: null
        });
      }
    } else {
      // Handle regular input change
      const { name, value } = e.target;
      setEditFormData({
        ...editFormData,
        [name]: value
      });

      // Clear error for this field
      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: null
        });
      }
    }
  };

  const filteredData = posts.filter((item) => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return (
      (!filters.treeName ||
        item.treeName.toLowerCase().includes(lowerSearchTerm)) &&
      (!filters.uploadedBy ||
        item.userName.toLowerCase().includes(lowerSearchTerm)) &&
      (!filters.location ||
        item.address.toLowerCase().includes(lowerSearchTerm))
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
      </div>
    );
  }

  if (!posts.length) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>No posts available.</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this tree post? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map/Location Modal */}
      <Dialog
        open={modalConfig.isOpen}
        onClose={closeModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {modalConfig.title}
          <IconButton
            onClick={closeModal}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {renderComponent()}
        </DialogContent>
      </Dialog>

      <div className="search-component">
        <div className="search-container">
          <div className="search-row">
            <div className="search-col">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleInputChange}
                className="search-input"
              />
            </div>
            <div className="search-col">
              <div className="filters">
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="treeName"
                      checked={filters.treeName}
                      onChange={handleCheckboxChange}
                      className="checkbox-input"
                    />
                    Tree Name
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="location"
                      checked={filters.location}
                      onChange={handleCheckboxChange}
                      className="checkbox-input"
                    />
                    Location
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="card-section">
          <div className="card-container">
            <div className="card-row">
              {filteredData.map((item, index) => (
                <div key={index} className="card-col">
                  <div className={`blogCardItem relative ${editingPostId === item.tree_id ? '' : 'h-auto'}`} style={{border:"1px solid #e0e0e0",borderRadius:"8px",boxShadow:"0 2px 4px rgba(0, 0, 0, 0.1)"}}>
                    {editingPostId === item.tree_id ? (
                      // Edit Form - Completely redesigned for maximum responsiveness
                      <div className="p-2 sm:p-3 md:p-4 bg-white overflow-visible">
                        <h3 className="text-base sm:text-lg font-bold text-center mb-2 text-gray-800 border-b pb-1">Edit Tree Information</h3>

                        {/* Image section - reduced height further */}
                        <div className="relative w-full h-32 sm:h-36 md:h-40 mb-2 border rounded overflow-hidden bg-gray-100 group">
                          {previewImage ? (
                            <>
                              <img
                                src={previewImage}
                                alt="Tree Preview"
                                className="w-full h-full object-cover"
                              />
                              {/* Simplified Overlay */}
                              <div
                                className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                                onClick={handleImageUploadClick}
                              >
                                <div className="bg-white rounded-full p-2">
                                  <FaUpload className="text-gray-700" />
                                </div>
                              </div>
                            </>
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center text-gray-400 cursor-pointer"
                              onClick={handleImageUploadClick}
                            >
                              <FaUpload className="mr-1" /> Select Image
                            </div>
                          )}

                          {/* Hidden input */}
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                          />
                        </div>

                        <div className="space-y-2">
                          {/* Form Fields - More compact layout */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700">ID:</label>
                              <input
                                type="text"
                                name="tree_id"
                                value={editFormData.tree_id}
                                readOnly
                                className="w-full p-1 border bg-gray-100 text-gray-700 text-xs sm:text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700">Name:</label>
                              <input
                                type="text"
                                name="treeName"
                                value={editFormData.treeName}
                                onChange={handleEditFormChange}
                                className={`w-full p-1 border text-xs sm:text-sm ${formErrors.treeName ? 'border-red-500' : ''}`}
                              />
                              {formErrors.treeName && (
                                <p className="text-red-500 text-xs">{formErrors.treeName}</p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700">Age:</label>
                              <Select
                                placeholder="Select Age *"
                                options={ageOptions}
                                styles={{
                                  container: (base) => ({
                                    ...base,
                                    width: "100%",
                                  }),
                                  control: (base) => ({
                                    ...base,
                                    minHeight: '30px',
                                    padding: 0,
                                    borderColor: formErrors.age ? '#f56565' : base.borderColor,
                                  }),
                                  valueContainer: (base) => ({
                                    ...base,
                                    padding: '0 8px',
                                  }),
                                  input: (base) => ({
                                    ...base,
                                    margin: 0,
                                    padding: 0,
                                  }),
                                  singleValue: (base) => ({
                                    ...base,
                                    fontSize: '0.75rem',
                                  }),
                                  placeholder: (base) => ({
                                    ...base,
                                    fontSize: '0.75rem',
                                  }),
                                  dropdownIndicator: (base) => ({
                                    ...base,
                                    padding: '0 4px',
                                  }),
                                  clearIndicator: (base) => ({
                                    ...base,
                                    padding: '0 4px',
                                  }),
                                  indicatorSeparator: () => ({
                                    display: 'none',
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    fontSize: '0.75rem',
                                  }),
                                  option: (base) => ({
                                    ...base,
                                    padding: '4px 8px',
                                  }),
                                }}
                                value={ageOptions.find(option => option.value === editFormData.age) || null}
                                onChange={(selectedOption) => handleEditFormChange(null, true, selectedOption, 'age')}
                              />
                              {formErrors.age && (
                                <p className="text-red-500 text-xs">{formErrors.age}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700">Species:</label>
                              <Select
                                placeholder="Plant Species *"
                                options={plantSpeciesOptions}
                                styles={{
                                  container: (base) => ({
                                    ...base,
                                    width: "100%",
                                  }),
                                  control: (base) => ({
                                    ...base,
                                    minHeight: '30px',
                                    padding: 0,
                                    borderColor: formErrors.species ? '#f56565' : base.borderColor,
                                  }),
                                  valueContainer: (base) => ({
                                    ...base,
                                    padding: '0 8px',
                                  }),
                                  input: (base) => ({
                                    ...base,
                                    margin: 0,
                                    padding: 0,
                                  }),
                                  singleValue: (base) => ({
                                    ...base,
                                    fontSize: '0.75rem',
                                  }),
                                  placeholder: (base) => ({
                                    ...base,
                                    fontSize: '0.75rem',
                                  }),
                                  dropdownIndicator: (base) => ({
                                    ...base,
                                    padding: '0 4px',
                                  }),
                                  clearIndicator: (base) => ({
                                    ...base,
                                    padding: '0 4px',
                                  }),
                                  indicatorSeparator: () => ({
                                    display: 'none',
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    fontSize: '0.75rem',
                                  }),
                                  option: (base) => ({
                                    ...base,
                                    padding: '4px 8px',
                                  }),
                                }}
                                value={plantSpeciesOptions.find(
                                  option => option.value === editFormData.speciesId ||
                                    option.label === editFormData.speciesLabel ||
                                    option.commonName === editFormData.speciesLabel
                                ) || null}
                                onChange={(selectedOption) => handleEditFormChange(null, true, selectedOption, 'species')}
                              />
                              {formErrors.species && (
                                <p className="text-red-500 text-xs">{formErrors.species}</p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700">Diameter (m):</label>
                              <input
                                type="text"
                                name="diameter"
                                value={editFormData.diameter}
                                onChange={handleEditFormChange}
                                className={`w-full p-1 border text-xs sm:text-sm ${formErrors.diameter ? 'border-red-500' : ''}`}
                              />
                              {formErrors.diameter && (
                                <p className="text-red-500 text-xs">{formErrors.diameter}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700">Height (m):</label>
                              <input
                                type="text"
                                name="height"
                                value={editFormData.height}
                                onChange={handleEditFormChange}
                                className={`w-full p-1 border text-xs sm:text-sm ${formErrors.height ? 'border-red-500' : ''}`}
                              />
                              {formErrors.height && (
                                <p className="text-red-500 text-xs">{formErrors.height}</p>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700">Location:</label>
                            <input
                              type="text"
                              name="address"
                              value={editFormData.address}
                              readOnly
                              onClick={() => openModal("Select Location", PopupMap)}
                              className={`w-full p-1 border text-xs sm:text-sm cursor-pointer ${formErrors.address ? 'border-red-500' : ''}`}
                            />
                            {formErrors.address && (
                              <p className="text-red-500 text-xs">{formErrors.address}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700">Planted On:</label>
                            <DatePicker
                              selected={editFormData.plantedOn ? new Date(editFormData.plantedOn) : null}
                              onChange={(date) => {
                                handleEditFormChange(
                                  { target: { name: "plantedOn", value: date ? date.toISOString() : "" } }
                                );
                              }}
                              showTimeSelect
                              dateFormat="Pp"
                              wrapperClassName="full-width-datepicker"
                              className={`w-full p-1 border text-xs sm:text-sm ${formErrors.plantedOn ? 'border-red-500' : ''}`}
                              placeholderText="Planted On"
                              maxDate={new Date()}
                              popperPlacement="auto"
                              popperModifiers={[
                                {
                                  name: 'preventOverflow',
                                  options: {
                                    rootBoundary: 'viewport',
                                    padding: 8
                                  }
                                },
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, 8]
                                  }
                                }
                              ]}
                            />
                            {formErrors.plantedOn && (
                              <p className="text-red-500 text-xs">{formErrors.plantedOn}</p>
                            )}
                          </div>


                          <div className="flex justify-center space-x-1 mt-2 pb-1">
                            <Button
                              onClick={handleSaveEdit}
                            // className="text-xs py-1 px-2 sm:py-1.5 sm:px-3 flex items-center"
                            >
                              <FaSave className="mr-1 text-xs" /> Save
                            </Button>
                            <Button
                              variant='cancel'
                              onClick={handleCancelEdit}
                            // className="text-xs py-1 px-2 sm:py-1.5 sm:px-3 flex items-center"
                            >
                              <FaTimes className="mr-1 text-xs" /> Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Normal Card View - No changes needed here
                      <>
                        <div className="relative blogCardImage">
                          <img
                            src={item.treeImageUrl}
                            alt={item.treeName}
                            className="card-image w-full h-48 object-cover"
                          />

                          {!editingPostId && (item.isVerified != 1) && (
                            <div className="absolute bottom-0 right-0 flex justify-right space-x-2 p-2">
                              <Button
                                className="flex items-center px-3 py-1 rounded text-white text-sm font-semibold bg-green-500 hover:bg-green-600"
                                onClick={() => handleEditClick(item)}
                                title="Edit tree information"
                              >
                                <FaEdit className="mr-1" /> Edit
                              </Button>
                              <Button
                                className="flex items-center px-3 py-1 rounded text-white text-sm font-semibold bg-red-500 hover:bg-red-600"
                                onClick={() => handleDeleteClick(item)}
                                title="Delete tree post"
                              >
                                <FaTrash className="mr-1" /> Delete
                              </Button>
                            </div>
                          )}
                        </div>

                        <span className="card-plantName" title={`Plant Name`}>
                          <span className="search-verified">
                            {
                              item.isVerified === 1 ? (
                                <FaCheckCircle color="green" />
                              ) : item.isVerified === 2 ? (
                                <FaTimesCircle style={{ color: 'red' }} />
                              ) : (
                                <FaCheckCircle color="yellow" />
                              )
                            }
                          </span>
                          &nbsp;&nbsp;{item.treeName}
                        </span>
                        <div className="blogCardDetails">
                          <span className="card-detail" title="Tree Id">
                            <FaTree style={{ display: "inline-flex", marginBottom: '2px' }} color="#002D62" size={14} /> {item.tree_id}
                          </span>
                          <span className="card-detail" title="Uploaded By">
                            <FaUserAlt style={{ display: "inline-flex", marginBottom: '2px' }} color="#002D62" size={14} /> {item.userName}
                          </span>
                          <span className="card-detail" title="Uploaded On">
                            <FaCalendarPlus style={{ display: "inline-flex", marginBottom: '2px' }} color="#002D62" size={14} /> {new Date(item.createdAt).toDateString()}
                          </span>
                          <span className="card-detail" title="Planted On">
                            <FaCalendarAlt style={{ display: "inline-flex", marginBottom: '2px' }} color="#002D62" size={14} /> {new Date(item.plantedOn).toDateString()}
                          </span>
                          <span className="card-detail" title="Species">
                            <FaPagelines style={{ display: "inline-flex", marginBottom: '2px' }} color="#002D62" size={14} /> {item.speciesLabel}
                          </span>
                          <span className="card-detail" title="Age">
                            <FaClock style={{ display: "inline-flex", marginBottom: '2px' }} color="#002D62" size={14} /> {item.age}
                          </span>
                          <span className="card-detail" title="Location">
                            <FaMapMarkerAlt style={{ display: "inline-flex", marginBottom: '2px' }} color="#002D62" size={14} /> {item.address}
                          </span>
                          {item.rejection_reason && (
                            <span className="card-detail text-red-600 font-bold text-sm ml-2">
                              <div className="flex items-start">
                                <FaExclamationTriangle className="text-red-500 mr-2 mt-1 flex-shrink-0" />
                                <div>
                                  <p className="font-bold text-red-700 text-sm">Rejection Reason:</p>
                                  <p className="text-red-700 text-sm">{item.rejection_reason}</p>
                                </div>
                              </div>
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProfilePosts;